import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageCircle, ArrowLeft, Loader2, User, Gift, Palmtree, Share2 , UserPlus} from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import { playConnectionSound } from "./GiftSystem";
import { GiftPicker } from "./GiftPickerV2";
import { getSceneImage } from "./IslandScene";
import { shareIsland } from "@/utils/shareIsland";

export function IslandView({ island, onBack, onConnectionChange }) {
  const navigate = useNavigate();
  const [isRinging, setIsRinging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageZoomed, setImageZoomed] = useState(false);
  const [showGiftPicker, setShowGiftPicker] = useState(false);
  const [details, setDetails] = useState({
    isConnected: island?.is_connected || false,
    isSelf: island?.is_own || island?.is_self || false,
    hasPending: island?.has_pending_request || false,
    direction: island?.request_direction || null,
    ownerName: island?.owner_name || "???",
    ownerPicture: island?.owner_picture || null,
    islandName: island?.island_name || "",
    isPremium: island?.is_premium || false,
    islandType: island?.island_type || "island_base",
  });

  useEffect(() => {
    if (!island?.user_id) return;
    const fetch = async () => {
      try {
        const res = await api.get(`/islands/${island.user_id}`);
        setDetails({
          isConnected: res.data.is_connected,
          isSelf: res.data.is_self || false,
          hasPending: res.data.has_pending_request,
          direction: res.data.request_direction,
          ownerName: res.data.owner_name || "???",
          ownerPicture: res.data.owner_picture,
          islandName: res.data.island_name,
          isPremium: island.is_premium || false,
          islandType: res.data.island_type || island.island_type || "island_base",
          isProPage: res.data.owner_profile_type === "professional",
        });
      } catch {}
      finally { setLoading(false); }
    };
    fetch();
  }, [island?.user_id, island?.is_premium, island?.island_type]);

  const handleRingDoorbell = async () => {
    if (details.hasPending || details.isConnected || details.isSelf) return;
    setIsRinging(true);
    playConnectionSound("send");
    try {
      const res = await api.post(`/islands/${island.user_id}/ring-doorbell`);
      if (res.data.status === "connected") {
        playConnectionSound("accept");
        toast.success("Collegamento stabilito!");
        setDetails(d => ({ ...d, isConnected: true, hasPending: false, ownerName: res.data.owner_name, ownerPicture: res.data.owner_picture }));
        onConnectionChange?.();
      } else {
        toast.success("Richiesta di collegamento inviata");
        setDetails(d => ({ ...d, hasPending: true, direction: "sent" }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setIsRinging(false); }
  };

  const islandImg = getSceneImage(details.islandType, details.isPremium);
  const knowsOwner = details.isSelf || details.isConnected;
  const avatarSrc = details.ownerPicture?.startsWith("/api")
    ? `${process.env.REACT_APP_BACKEND_URL}${details.ownerPicture}`
    : details.ownerPicture;
  const circleImg = knowsOwner && avatarSrc ? avatarSrc : islandImg;
  const borderColor = details.isSelf ? "#ec4899" : details.isConnected ? "#10b981" : details.isPremium ? "#fbbf24" : "#0ea5e9";
  const glassBtn = { background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.16)" };

  return (
    <div className="relative pointer-events-auto" data-testid="island-view-overlay">
      {/* Fullscreen zoomed image */}
      {imageZoomed && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
          onClick={() => setImageZoomed(false)}
        >
          <button className="absolute top-4 right-4 p-2 bg-white/10 rounded-full" onClick={() => setImageZoomed(false)}>
            <ArrowLeft className="w-5 h-5 text-white" />
          </button>
          <img
            src={circleImg}
            alt={details.islandName}
            className="w-[85vw] max-w-[400px] max-h-[70vh] object-contain rounded-2xl"
            style={{ filter: "drop-shadow(0 0 40px rgba(255,255,255,0.1))" }}
            draggable={false}
          />
          <p className="absolute bottom-8 text-white/60 text-sm font-medium">{knowsOwner ? details.ownerName : details.islandName}</p>
        </div>
      )}

      {/* Back button */}
      <div className="px-3 pb-2 flex justify-start">
        <button onClick={onBack} className="p-2.5 rounded-full backdrop-blur-md active:scale-90 transition-transform"
                style={glassBtn} data-testid="island-view-back">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Avatar circle: owner photo when known, island otherwise */}
      <div className="flex justify-center -mb-10 relative z-10">
        <div className="relative cursor-pointer" onClick={() => setImageZoomed(true)}>
          <div style={{
            width: 84, height: 84, borderRadius: "50%", overflow: "hidden",
            border: `3px solid ${borderColor}`,
            boxShadow: `0 0 20px ${borderColor}55, 0 4px 16px rgba(0,0,0,0.4)`,
            background: "#0a1626",
          }}>
            {circleImg ? (
              <img src={circleImg} alt="" className="w-full h-full object-cover" draggable={false} />
            ) : (
              <div className="w-full h-full flex items-center justify-center"><User className="w-8 h-8 text-cyan-300/60" /></div>
            )}
          </div>
          <div className="absolute inset-0 rounded-full" style={{
            border: `2px solid ${borderColor}`,
            animation: "ring-pulse 2s ease-out infinite",
            opacity: 0.5,
          }} />
        </div>
      </div>

      {/* Bottom glass card — transparent so the island stays visible */}
      <div className="rounded-t-3xl pt-12 pb-5 px-4"
           style={{ background: "rgba(8,12,24,0.5)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
                    borderTop: "1px solid rgba(255,255,255,0.14)" }}>
        {/* Owner name → island name */}
        <div className="text-center mb-3">
          <h2 className="text-lg font-extrabold text-white flex items-center justify-center gap-2" data-testid="island-owner-name">
            {knowsOwner ? details.ownerName : "???"}
            {details.isSelf && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-pink-300"
                    style={{ background: "rgba(236,72,153,0.15)", border: "1px solid rgba(236,72,153,0.35)" }}>Tu</span>
            )}
            {details.isConnected && !details.isSelf && (
              <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold text-emerald-300"
                    style={{ background: "rgba(16,185,129,0.15)", border: "1px solid rgba(16,185,129,0.35)" }}>Collegato</span>
            )}
          </h2>
          <p className="text-sm text-white/65 flex items-center justify-center gap-1.5 mt-0.5" data-testid="island-view-name">
            <Palmtree className="w-3.5 h-3.5 text-emerald-300" />
            {details.islandName}
          </p>
        </div>

        {/* Actions */}
        {loading ? (
          <div className="flex justify-center py-3">
            <Loader2 className="w-5 h-5 text-cyan-300 animate-spin" />
          </div>
        ) : details.isSelf ? (
          <div className="space-y-2">
            <button onClick={() => navigate('/app/profile')} className="w-full py-2.5 text-sm font-semibold text-white rounded-xl active:scale-[0.98] transition-transform" style={{ background: "linear-gradient(135deg, #ec4899, #f97316)" }}>
              Vai al tuo Profilo
            </button>
            <button
              onClick={async () => {
                try {
                  const r = await shareIsland(details.islandType, details.islandName);
                  if (r === "downloaded") toast.success("Immagine scaricata: condividila sui social!");
                } catch { toast.error("Condivisione non riuscita"); }
              }}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-cyan-200 active:scale-[0.98] transition-transform"
              style={{ background: "rgba(34,211,238,0.1)", border: "1px solid rgba(34,211,238,0.35)" }}
              data-testid="share-island-btn"
            >
              <Share2 className="w-4 h-4" /> Condividi la tua Isola
            </button>
          </div>
        ) : details.isConnected ? (
          <div className="space-y-2">
            <div className="flex gap-2">
              <button onClick={() => navigate(`/app/user/${island.user_id}`)} className="flex-1 py-2.5 text-sm font-semibold text-white rounded-xl" style={{ background: "linear-gradient(135deg, #0891b2, #0369a1)" }} data-testid="view-profile-btn">
                Vedi Profilo
              </button>
              <button onClick={() => navigate(`/app/chat/${island.user_id}`)} className="py-2.5 px-4 text-white rounded-xl" style={{ background: "linear-gradient(135deg, #ec4899, #f97316)" }} data-testid="send-message-btn">
                <MessageCircle className="w-5 h-5" />
              </button>
            </div>
            <button onClick={() => setShowGiftPicker(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white transition-transform active:scale-[0.98]" style={{ background: "linear-gradient(135deg, #EC4899, #F97316)" }} data-testid="island-gift-btn">
              <Gift className="w-4 h-4" /> Lascia una Gift
            </button>
          </div>
        ) : details.hasPending ? (
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl"
                 style={{ background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)" }}>
              <Bell className="w-5 h-5 text-amber-300" style={{ animation: "bell-ring 1s ease-in-out infinite" }} />
              <div className="text-left">
                <p className="font-semibold text-white text-sm">
                  {details.direction === "sent" ? "Richiesta inviata" : "Vuole conoscerti!"}
                </p>
                <p className="text-[10px] text-white/55">
                  {details.direction === "sent" ? "In attesa di risposta..." : "Vai nelle richieste per accettare"}
                </p>
              </div>
            </div>
            <button onClick={() => setShowGiftPicker(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-pink-300 active:scale-[0.98] transition-transform"
                    style={{ background: "rgba(236,72,153,0.1)", border: "1px solid rgba(236,72,153,0.35)" }} data-testid="island-gift-btn">
              <Gift className="w-4 h-4" /> Lascia una Gift
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <button
              onClick={handleRingDoorbell}
              disabled={isRinging}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-white font-semibold text-sm transition-transform active:scale-[0.98]"
              style={{ background: "linear-gradient(135deg, #f59e0b, #f97316)" }}
              data-testid="island-doorbell-btn"
            >
              {isRinging ? <Loader2 className="w-5 h-5 animate-spin" /> : <><UserPlus className="w-5 h-5" />{details.isProPage ? "Segui" : "Richiedi collegamento"}</>}
            </button>
            <button onClick={() => setShowGiftPicker(true)} className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-pink-300 active:scale-[0.98] transition-transform"
                    style={{ background: "rgba(236,72,153,0.1)", border: "1px solid rgba(236,72,153,0.35)" }} data-testid="island-gift-btn">
              <Gift className="w-4 h-4" /> Lascia una Gift
            </button>
          </div>
        )}
      </div>

      {/* Gift Picker for Island */}
      <GiftPicker
        isOpen={showGiftPicker}
        onClose={() => setShowGiftPicker(false)}
        recipientId={island?.user_id}
        recipientName={details.islandName}
        onGiftSent={() => {
          toast.success(`Gift lasciato su ${details.islandName}!`);
          setShowGiftPicker(false);
        }}
      />

      <style>{`
        @keyframes ring-pulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes bell-ring {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-12deg); }
          40% { transform: rotate(12deg); }
          60% { transform: rotate(-8deg); }
          80% { transform: rotate(8deg); }
        }
      `}</style>
    </div>
  );
}

export default IslandView;
